import { Controller } from "@hotwired/stimulus"
import { setHidden } from "../lib/display_toggle"

export default class extends Controller {
  static targets = [
    "panel",
    "toggleBtn",
    "filterBadge",
    "chipsBar",
    "filterField",
    "saveBtn",
    "searchInput",
    "searchClear",
    "dateFrom",
    "dateTo",
    "dateRangeBtn",
    "dateRangePanel",
    "dateRangeLabel"
  ]

  static values = {
    pageIdentifier: { type: String, default: "leads" }
  }

  // A nested menu still open inside the panel owns the next Escape (the same
  // selector lib/filter_card_disclosure.js keys on; kept local so this
  // controller adds no import edge to the shared audit graph).
  static NESTED_OPEN_MENU = ".gl-multi-select-popover.open"

  connect() {
    this._listeningForEscape = false
    this._onClickOutside = this.handleClickOutside.bind(this)
    this._onKeydown = this.handleKeydown.bind(this)
    this._onBeforeCache = this.beforeCache.bind(this)
    this._onSearchInput = () => this.updateFilterState()
    document.addEventListener("click", this._onClickOutside, true)
    document.addEventListener("turbo:before-cache", this._onBeforeCache)
    // The open/closed flags are READ from the DOM, never assumed closed: a
    // Turbo snapshot restored from the cache (Back) or any other markup that
    // arrives already `.open` reconnects this controller against a panel the
    // user can see. Assuming closed left that panel with no Escape listener,
    // no outside-click dismissal and a trigger whose next click "opened" it
    // again (#14692). beforeCache() keeps the snapshot closed in the first
    // place; this is the belt to that brace.
    this.filtersOpen = this.hasPanelTarget && this.panelTarget.classList.contains("open")
    this.dateRangeOpen = this.hasDateRangePanelTarget && this.dateRangePanelTarget.classList.contains("open")
    this.syncTriggerState()
    this.syncEscapeListener()
    this.updateFilterState()
    this.observeSearchInput()
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside, true)
    document.removeEventListener("turbo:before-cache", this._onBeforeCache)
    this.stopListeningForEscape()
    if (this._onSearchInput && this.hasSearchInputTarget) {
      this.searchInputTarget.removeEventListener("input", this._onSearchInput)
    }
  }

  // ── Filters dropdown ───────────────────────────────────────────────

  toggle() {
    this.filtersOpen ? this.close() : this.open()
  }

  open() {
    this.closeDateRange()
    this.filtersOpen = true
    if (this.hasPanelTarget) this.panelTarget.classList.add("open")
    this.syncTriggerState()
    this.syncEscapeListener()
  }

  close() {
    this.filtersOpen = false
    if (this.hasPanelTarget) this.panelTarget.classList.remove("open")
    this.syncTriggerState()
    this.syncEscapeListener()
  }

  // ── Date range dropdown ────────────────────────────────────────────

  toggleDateRange() {
    this.dateRangeOpen ? this.closeDateRange() : this.openDateRange()
  }

  openDateRange() {
    this.close()
    this.dateRangeOpen = true
    if (this.hasDateRangePanelTarget) this.dateRangePanelTarget.classList.add("open")
    this.syncTriggerState()
    this.syncEscapeListener()
  }

  closeDateRange() {
    this.dateRangeOpen = false
    if (this.hasDateRangePanelTarget) this.dateRangePanelTarget.classList.remove("open")
    this.syncTriggerState()
    this.syncEscapeListener()
  }

  // Each trigger paints `active` and announces `aria-expanded` from the ONE
  // flag its surface keeps, so every route — toggle, Escape, Done / X, an
  // outside click, a reconnect over restored markup — leaves the trigger
  // telling the truth about the panel it controls (#14692).
  syncTriggerState() {
    if (this.hasToggleBtnTarget) {
      this.toggleBtnTarget.classList.toggle("active", this.filtersOpen)
      this.toggleBtnTarget.setAttribute("aria-expanded", String(this.filtersOpen))
    }
    if (this.hasDateRangeBtnTarget) {
      this.dateRangeBtnTarget.classList.toggle("active", this.dateRangeOpen)
      this.dateRangeBtnTarget.setAttribute("aria-expanded", String(this.dateRangeOpen))
    }
  }

  // Turbo snapshots the live DOM for its cache right before it navigates
  // away. An open Filters / Date panel in that snapshot comes back open on
  // Back (#14692) — close both so the cached page is the closed page. The
  // DOM-read in connect() then has nothing to reconcile on restore.
  beforeCache() {
    this.close()
    this.closeDateRange()
  }

  // -- Explicit dismissal (Done, header X) -----------------------------
  // #14692: an explicit dismissal closes the surface AND returns focus to
  // the trigger that opened it (WAI-ARIA dialog pattern; A11Y-2 -- focus must
  // land somewhere visible, not on the control that just disappeared).
  // Outside-click keeps its own path through close(): the click chose its
  // own target. Escape already returns focus via handleKeydown.
  dismiss() {
    const surface = this.openSurface()
    if (!surface) return
    surface.close()
    surface.trigger?.focus({ preventScroll: true })
  }

  // ── Escape dismissal (#14545) ──────────────────────────────────────
  //
  // Escape closes whichever owned surface is open — Filters or Date, never
  // both, since open() / openDateRange() keep them mutually exclusive — and
  // hands focus back to THAT surface's trigger (A11Y-2: focus must land
  // somewhere visible, not on the control that just disappeared). Nothing
  // else moves: values apply on change, so there is no draft to restore and
  // nothing is cleared; Done, the header X and an outside click keep their
  // own paths.
  //
  // The listener is document-level (focus may sit on <body> after a click on
  // the panel's padding) and lives ONLY while a surface is open, so a closed
  // panel — or a disconnected controller — holds no key handler. It runs in
  // the CAPTURE phase, like lib/filter_card_disclosure.js: a nested menu that
  // is still open inside the panel closes itself on Escape without calling
  // preventDefault, so seeing it open before it reacts is the only way to
  // give it the key first. A handler that ran earlier and claimed the key
  // (defaultPrevented) is honoured too. A native <select> needs no special
  // case: an OPEN picklist consumes Escape inside the browser before the page
  // sees the keydown, and a closed select's Escape is just a control inside
  // the panel, dismissed like any other.

  handleKeydown(event) {
    if (event.key !== "Escape" || event.defaultPrevented) return
    const surface = this.openSurface()
    if (!surface) return
    if (surface.panel?.querySelector(this.constructor.NESTED_OPEN_MENU)) return
    event.preventDefault()
    surface.close()
    surface.trigger?.focus({ preventScroll: true })
  }

  openSurface() {
    if (this.filtersOpen) {
      return {
        panel: this.hasPanelTarget ? this.panelTarget : null,
        trigger: this.hasToggleBtnTarget ? this.toggleBtnTarget : null,
        close: () => this.close()
      }
    }
    if (this.dateRangeOpen) {
      return {
        panel: this.hasDateRangePanelTarget ? this.dateRangePanelTarget : null,
        trigger: this.hasDateRangeBtnTarget ? this.dateRangeBtnTarget : null,
        close: () => this.closeDateRange()
      }
    }
    return null
  }

  syncEscapeListener() {
    const shouldListen = this.filtersOpen || this.dateRangeOpen
    if (shouldListen === this._listeningForEscape) return
    if (shouldListen) {
      document.addEventListener("keydown", this._onKeydown, true)
      this._listeningForEscape = true
    } else {
      this.stopListeningForEscape()
    }
  }

  stopListeningForEscape() {
    if (!this._listeningForEscape) return
    document.removeEventListener("keydown", this._onKeydown, true)
    this._listeningForEscape = false
  }

  onDatePreset(event) {
    const preset = event.currentTarget.dataset.preset
    const presetLabels = {
      today: "Today",
      yesterday: "Yesterday",
      last_7_days: "Last 7 Days",
      last_30_days: "Last 30 Days",
      this_month: "This Month",
      last_month: "Last Month"
    }

    if (this.hasDateRangeLabelTarget) {
      this.dateRangeLabelTarget.textContent = presetLabels[preset] || preset
    }

    // Update active preset button
    this.dateRangePanelTarget?.querySelectorAll(".gl-date-range-preset").forEach(btn => {
      btn.classList.toggle("active", btn.dataset.preset === preset)
    })

    this.closeDateRange()
    this.updateFilterState()
  }

  onDateApply() {
    if (!this.hasDateFromTarget || !this.hasDateToTarget) return

    const from = this.dateFromTarget.value
    const to = this.dateToTarget.value
    if (from && to) {
      if (this.hasDateRangeLabelTarget) {
        this.dateRangeLabelTarget.textContent = `${this.formatShortDate(from)} – ${this.formatShortDate(to)}`
      }
      // Clear active preset since custom range was set
      this.dateRangePanelTarget?.querySelectorAll(".gl-date-range-preset").forEach(btn => {
        btn.classList.remove("active")
      })
    }

    this.closeDateRange()
    this.updateFilterState()
  }

  onDateClear() {
    if (this.hasDateRangeLabelTarget) {
      this.dateRangeLabelTarget.textContent = "Date Range"
    }
    // Clear active preset
    this.dateRangePanelTarget?.querySelectorAll(".gl-date-range-preset").forEach(btn => {
      btn.classList.remove("active")
    })
    this.closeDateRange()
    this.updateFilterState()
  }

  formatShortDate(dateStr) {
    if (!dateStr) return ""
    const parts = dateStr.split("-")
    if (parts.length === 3) {
      return `${parseInt(parts[1])}/${parseInt(parts[2])}/${parts[0].slice(2)}`
    }
    return dateStr
  }

  // ── Click outside to close ─────────────────────────────────────────

  handleClickOutside(event) {
    if (this.filtersOpen && this.hasPanelTarget) {
      const wrapper = this.panelTarget.closest(".gl-filter-dropdown-wrapper")
      if (wrapper && !wrapper.contains(event.target)) {
        this.close()
      }
    }

    if (this.dateRangeOpen && this.hasDateRangePanelTarget) {
      const wrapper = this.dateRangePanelTarget.closest(".gl-date-range-wrapper")
      if (wrapper && !wrapper.contains(event.target)) {
        this.closeDateRange()
      }
    }
  }

  // ── Filter state management ────────────────────────────────────────

  onFilterChange() {
    this.updateFilterState()
  }

  clearSearch() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ""
      this.searchInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }
    this.updateSearchClear()
  }

  clearAll() {
    this.filterFieldTargets.forEach(field => { field.value = "" })
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""

    this.filterFieldTargets.forEach(field => {
      field.dispatchEvent(new Event("change", { bubbles: true }))
    })

    document.dispatchEvent(new CustomEvent("filters:cleared"))
    this.updateFilterState()
    this.updateSearchClear()
  }

  openSaveModal() {
    const saveModal = document.getElementById(`saveFilterModal-${this.pageIdentifierValue}`)
    if (!saveModal || !window.bootstrap?.Modal) return

    this._populateSaveModalPreview(saveModal)

    const modal = window.bootstrap.Modal.getOrCreateInstance(saveModal)
    modal.show()
  }

  _populateSaveModalPreview(modalEl) {
    const preview = modalEl.querySelector(".gl-save-filter-preview")
    if (!preview) return

    const chips = this._collectAllChipsForSave()

    if (chips.length === 0) {
      setHidden(preview, true)
      preview.innerHTML = ""
      return
    }

    setHidden(preview, false)
    preview.innerHTML =
      '<div class="gl-save-filter-preview-label">Active filters:</div>' +
      '<div class="gl-save-filter-preview-chips">' +
      chips.map(chip =>
        `<span class="gl-filter-chip gl-filter-chip--readonly">
          <span class="gl-filter-chip-label">${chip.label}:</span>
          <span class="gl-filter-chip-value">${this.escapeHtml(chip.displayValue)}</span>
        </span>`
      ).join("") +
      '</div>'
  }

  updateFilterState() {
    const activeCount = this.countActiveFilters()
    this.updateBadge(activeCount)
    this.updateChips()
    this.updateSaveButton(activeCount)
    this.updateSearchClear()
  }

  countActiveFilters() {
    let count = 0
    this.filterFieldTargets.forEach(field => {
      if (field.value && field.value !== "") count++
    })
    if (this.hasSearchInputTarget && this.searchInputTarget.value.trim() !== "") count++
    return count
  }

  _hasActiveDateRange() {
    if (this.hasDateFromTarget && this.hasDateToTarget) {
      return !!(this.dateFromTarget.value && this.dateToTarget.value)
    }
    if (this.hasDateRangeLabelTarget) {
      const label = this.dateRangeLabelTarget.textContent.trim()
      return label !== "" && label !== "Date Range"
    }
    return false
  }

  _getDateRangeLabel() {
    if (this.hasDateRangeLabelTarget) {
      const label = this.dateRangeLabelTarget.textContent.trim()
      if (label && label !== "Date Range") return label
    }
    if (this.hasDateFromTarget && this.hasDateToTarget &&
        this.dateFromTarget.value && this.dateToTarget.value) {
      return `${this.formatShortDate(this.dateFromTarget.value)} – ${this.formatShortDate(this.dateToTarget.value)}`
    }
    return null
  }

  updateBadge(count) {
    if (!this.hasFilterBadgeTarget) return
    if (count > 0) {
      this.filterBadgeTarget.textContent = count
      setHidden(this.filterBadgeTarget, false)
    } else {
      setHidden(this.filterBadgeTarget, true)
    }
  }

  updateChips() {
    if (!this.hasChipsBarTarget) return
    const chips = this._collectFilterChips()

    if (chips.length === 0) {
      setHidden(this.chipsBarTarget, true)
      this.chipsBarTarget.innerHTML = ""
      return
    }

    setHidden(this.chipsBarTarget, false)
    this.chipsBarTarget.innerHTML = chips.map(chip =>
      `<span class="gl-filter-chip" data-param="${chip.param}">
        <span class="gl-filter-chip-label">${chip.label}:</span>
        <span class="gl-filter-chip-value">${this.escapeHtml(chip.displayValue)}</span>
        <button type="button" class="gl-filter-chip-remove" data-action="click->filter-panel#removeChip" data-param="${chip.param}" aria-label="Remove filter" data-gl-tooltip="Remove filter">
          <i class="ph ph-x"></i>
        </button>
      </span>`
    ).join("")
  }

  _collectFilterChips() {
    const chips = []

    this.filterFieldTargets.forEach(field => {
      if (!field.value || field.value === "") return
      const label = field.dataset.filterLabel || "Filter"
      const displayValue = field.tagName === "SELECT"
        ? (field.selectedOptions[0]?.textContent || field.value)
        : field.value
      const param = field.dataset.leadsTableFilterParam || field.dataset.filterParam
      chips.push({ label, displayValue, param })
    })

    if (this.hasSearchInputTarget && this.searchInputTarget.value.trim()) {
      chips.push({ label: "Search", displayValue: this.searchInputTarget.value.trim(), param: "search_filter" })
    }

    return chips
  }

  _collectAllChipsForSave() {
    const chips = this._collectFilterChips()

    const dateLabel = this._getDateRangeLabel()
    if (dateLabel) {
      chips.push({ label: "Date", displayValue: dateLabel, param: "date_range" })
    }

    return chips
  }

  removeChip(event) {
    const param = event.currentTarget.dataset.param
    if (param === "search_filter") {
      if (this.hasSearchInputTarget) {
        this.searchInputTarget.value = ""
        this.searchInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
      }
    } else {
      const field = this.filterFieldTargets.find(f => (f.dataset.leadsTableFilterParam || f.dataset.filterParam) === param)
      if (field) {
        field.value = ""
        field.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }
    this.updateFilterState()
  }

  updateSaveButton(count) {
    if (!this.hasSaveBtnTarget) return
    const hasAnything = count > 0 || this._hasActiveDateRange()
    setHidden(this.saveBtnTarget, !hasAnything)
  }

  observeSearchInput() {
    if (!this.hasSearchInputTarget) return
    this.searchInputTarget.addEventListener("input", this._onSearchInput)
  }

  updateSearchClear() {
    if (!this.hasSearchClearTarget || !this.hasSearchInputTarget) return
    setHidden(this.searchClearTarget, !this.searchInputTarget.value.trim())
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
